<!--
  BaseDialog.vue — Shared dialog wrapper enforcing consistent layout across the app.

  Wraps v-dialog + v-card with a standardised toolbar, scrollable content area,
  and action bar. All dialogs should use this component instead of rolling their
  own v-dialog/v-card/v-toolbar boilerplate.

  Props:
    modelValue    — v-model binding for open/close state
    title         — Toolbar title text
    subtitle      — Optional subtitle shown after title (smaller text)
    maxWidth      — Desktop max-width (default 500)
    persistent    — Prevent dismiss on outside click
    scrollable    — Enable scrollable dialog (default true)
    saveText      — Primary action button label (default "Save")
    saveColor     — Primary action button color (default "primary")
    saveLoading   — Show loading spinner on save button
    saveDisabled  — Disable save button
    showSave      — Show/hide primary action button (default true)
    cancelText    — Left button label (default "Close")
    cancelAction  — What happens on cancel click: "close" (default) or "emit"
    toolbarColor  — Toolbar background colour (default "primary")
    headerStyle   — "toolbar" (default) or "card-title" for a simpler card-title header

  Slots:
    default       — Main content area (inside v-card-text)
    actions       — Extra action buttons (rendered before save button)
    toolbar-extra — Extra toolbar items (rendered before close icon)

  Events:
    update:modelValue — v-model support
    save              — Emitted when primary action button is clicked
    cancel            — Emitted when cancel button is clicked (only if cancelAction="emit")
-->
<template>
  <v-dialog
    :model-value="modelValue"
    @update:model-value="onDialogUpdate"
    :fullscreen="mobile"
    :max-width="mobile ? undefined : maxWidth"
    :persistent="effectivePersistent"
    :scrollable="scrollable"
    :height="!mobile && fixedHeight ? '90vh' : undefined"
    :min-height="!mobile && !fixedHeight ? minHeight || undefined : undefined"
    :max-height="!mobile && !fixedHeight ? maxHeight || '90vh' : undefined"
  >
    <v-card class="d-flex flex-column">
      <!-- Card-title header (simpler, no colored toolbar) -->
      <v-card-title
        v-if="headerStyle === 'card-title'"
        class="d-flex align-center pa-4 pb-0 flex-shrink-0"
        :class="{ 'card-title-wrap': wrapTitle }"
      >
        <div class="d-flex align-center" :class="titleIconColor ? `text-${titleIconColor}` : ''">
          <v-icon v-if="titleIcon" :icon="titleIcon" :color="titleIconColor" size="small" class="mr-1" />
          <span :class="wrapTitle ? '' : 'text-truncate'">{{ title }}</span>
          <span v-if="subtitle" class="text-body-2 ml-2" style="opacity: 0.7">{{ subtitle }}</span>
        </div>
        <v-spacer />
        <slot name="toolbar-extra" />
        <v-btn icon :size="mobile ? 'default' : 'small'" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <!-- Standardised toolbar with title and close icon (default) -->
      <v-toolbar v-else :color="toolbarColor" density="compact" class="flex-shrink-0" :class="{ 'toolbar-title-wrap': wrapTitle }">
        <v-toolbar-title>
          <v-icon v-if="titleIcon" key="dialog-title-icon" :icon="titleIcon" :color="titleIconColor" size="small" class="mr-1" />{{ title }}
          <span v-if="subtitle" class="text-body-2 ml-2" style="opacity: 0.7">{{ subtitle }}</span>
        </v-toolbar-title>
        <v-spacer />
        <slot name="toolbar-extra" />
        <v-btn icon :size="mobile ? 'default' : 'small'" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Fixed content area above scroll — use for fields that should not scroll -->
      <div v-if="$slots['header-content']" class="flex-shrink-0 px-4 pt-4">
        <slot name="header-content" />
      </div>

      <!-- Scrollable content area — min-height:0 allows flex child to shrink and scroll -->
      <v-card-text class="flex-grow-1 overflow-y-auto" style="min-height: 0">
        <slot />
      </v-card-text>

      <!-- Action bar — shown when buttons are visible or actions slot has content -->
      <v-card-actions v-if="showCancel || showSave || $slots.actions" class="flex-shrink-0" :class="{ 'justify-center': !showCancel && !showSave && $slots.actions }">
        <v-btn v-if="showCancel" :color="cancelColor" :variant="cancelVariant" :class="cancelClass" @click="onCancel">{{ cancelText }}</v-btn>
        <v-spacer v-if="showCancel && showSave" />
        <slot name="actions" />
        <v-btn
          v-if="showSave"
          :color="saveColor"
          variant="flat"
          :loading="saveLoading"
          :disabled="saveDisabled"
          @click="$emit('save')"
        >
          {{ saveText }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { computed } from "vue";
import { useDisplay } from "vuetify";

const { mobile } = useDisplay();

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: "" },
  subtitle: { type: String, default: null },
  maxWidth: { type: [Number, String], default: 500 },
  persistent: { type: Boolean, default: true },
  scrollable: { type: Boolean, default: true },
  saveText: { type: String, default: "Save" },
  saveColor: { type: String, default: "primary" },
  saveLoading: { type: Boolean, default: false },
  saveDisabled: { type: Boolean, default: false },
  showSave: { type: Boolean, default: true },
  showCancel: { type: Boolean, default: true },
  cancelText: { type: String, default: "Close" },
  cancelColor: { type: String, default: "default" },
  cancelVariant: { type: String, default: "outlined" },
  // "close" = cancel button closes the dialog; "emit" = cancel button emits @cancel
  cancelAction: { type: String, default: "close" },
  toolbarColor: { type: String, default: "primary" },
  // Optional icon prepended to the title text
  titleIcon: { type: String, default: null },
  titleIconColor: { type: String, default: undefined },
  headerStyle: { type: String, default: "card-title" },
  wrapTitle: { type: Boolean, default: false },
  // Async guard called before closing — return false to cancel close
  beforeClose: { type: Function, default: null },
  // When true, dialog uses fixed height (90vh) — content scrolls, dialog never resizes
  fixedHeight: { type: Boolean, default: false },
  // Optional min-height passed to v-dialog — dialog won't shrink below this
  minHeight: { type: [Number, String], default: null },
  // Optional max-height passed to v-dialog — dialog won't grow above this
  maxHeight: { type: [Number, String], default: null },
});

const emit = defineEmits(["update:modelValue", "save", "cancel"]);

// Push cancel to the right when there is no save button
const cancelClass = computed(() => (!props.showSave ? "ms-auto" : ""));

// Persistent by default — prevents accidental form data loss on outside click
// ESC / outside-click via onDialogUpdate without blocking the attempt
const effectivePersistent = computed(() => props.persistent);

/** Close the dialog, checking beforeClose guard first */
async function close() {
  if (props.beforeClose) {
    const canClose = await props.beforeClose();
    if (!canClose) return;
  }
  emit("update:modelValue", false);
}

/** Handle v-dialog update:model-value — route close attempts through beforeClose guard */
async function onDialogUpdate(val) {
  if (!val && props.beforeClose) {
    const canClose = await props.beforeClose();
    if (!canClose) return;
  }
  emit("update:modelValue", val);
}

/** Handle cancel button click — either closes dialog or emits event */
function onCancel() {
  if (props.cancelAction === "emit") {
    emit("cancel");
  } else {
    close();
  }
}
</script>

<style scoped>
/* When wrapTitle is set, override Vuetify's internal truncation on toolbar title */
.toolbar-title-wrap :deep(.v-toolbar-title) {
  flex: 1 1 auto;
}
.toolbar-title-wrap :deep(.v-toolbar-title__placeholder) {
  overflow: visible;
  text-overflow: unset;
  white-space: normal;
}
/* Same wrapping behavior for the default card-title header (long entity
   names) — the title's div grows to fill the row instead of being clipped */
.card-title-wrap {
  flex-wrap: wrap;
}
.card-title-wrap > div:first-child {
  min-width: 0;
  flex: 1 1 auto;
  white-space: normal;
  word-break: break-word;
}
/* Vuetify 4: v-card-title sets overflow:hidden which clips icons — restore visible */
:deep(.v-card-title) {
  overflow: visible;
}
</style>
